import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { ALL_PAGE_SIZE } from '../../components/listDefaults';
import { FormulaEditorDialog, type FormulaSymbol } from './FormulaEditorDialog';

/** An offered field — a live dbo.DailyQcDataTable row. */
interface AvailableRow {
  oid: string;
  fieldName?: string;
  mappedProperty?: string;
}

/** A configured field — a live dbo.DailyQCLogBookBuilderSelected row. */
interface SelectedRow {
  oid: string;
  name?: string;
  caption?: string;
  mappedProperty?: string;
  assignedValue?: string;
  width?: number | null;
  sort?: number | null;
  freeze?: boolean;
  default?: boolean;
  symbol?: string;
  formula?: boolean;
  formulaText?: string;
}

/**
 * The legacy "Field Configuration" area of the Daily QC Log Book Builder detail —
 * a dual list: every field the lab has defined (dbo.DailyQcDataTable, maintained by
 * Settings → Analysis → Daily Qc → Data Table) on the left, the ones configured
 * into THIS template (dbo.DailyQCLogBookBuilderSelected) on the right, with the
 * green + / red − between them.
 *
 * Available is computed as "not already selected", so a field moves back into the
 * left list the moment it is removed from the right — there is no third state to
 * keep in sync.
 *
 * Both panes are the SHARED <AppDataGrid> (standing rules 7 + 9), so they inherit
 * the app's header, row hover, column filter popups, resizable columns, the
 * standard footer (page X of N · page-size dropdown) and the Export / Maximize
 * cluster — which is also what legacy shows, since its Selected grid paginates at
 * 20 and prints its own row count.
 *
 * The Selected grid edits IN PLACE through the shared click-to-edit `edit` config:
 * no editor is drawn at rest, so the grid reads as a grid rather than as a wall of
 * input boxes (user feedback 2026-09-24, comparing against G5).
 */
export function DailyQcFieldConfigGrid({ parentId, viewOnly, flushRef }: {
  parentId: string | null;
  viewOnly: boolean;
  /** Set by <SettingsDetailPage> so Save can write the buffered drafts itself. */
  flushRef?: MutableRefObject<((id: string) => Promise<void>) | null>;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [available, setAvailable] = useState<AvailableRow[]>([]);
  const [selected, setSelected] = useState<SelectedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickLeft, setPickLeft] = useState<Set<string | number>>(new Set());
  const [pickRight, setPickRight] = useState<Set<string | number>>(new Set());
  /** The Selected row whose formula is being edited (null = the popup is closed). */
  const [fxRow, setFxRow] = useState<SelectedRow | null>(null);

  /** Fields chosen on a NEW record, held here until the template exists — DRAFTS,
   *  fully editable like any saved row.
   *
   *  A configured field is stored against its template's Oid, which a record does
   *  not have until its first save, so on New there is nothing to attach a row to.
   *  Rather than disable the panel, the picks AND their edits are buffered and
   *  written the moment the template is saved. `oid` here is the SOURCE
   *  DailyQcDataTable Oid (what the add endpoint takes), not a Selected-row Oid. */
  const [pending, setPending] = useState<SelectedRow[]>([]);
  /** Latest `pending`, read by the flush (which Save calls, not React). */
  const pendingRef = useRef<SelectedRow[]>([]);
  pendingRef.current = pending;
  /** Latest `parentId`, so `apply` can branch without becoming unstable (a new
   *  identity there would rebuild the columns and reset their widths). */
  const parentIdRef = useRef<string | null>(parentId);
  parentIdRef.current = parentId;
  /** Latest `selected`, readable from a stable callback (see `apply`). */
  const selectedRef = useRef<SelectedRow[]>([]);
  selectedRef.current = selected;

  /** Last Formula-cell click, for the re-render-proof double-click (see below). */
  const lastFxClick = useRef<{ oid: string; at: number } | null>(null);

  /** Open the Formula Editor on the SECOND of two quick clicks on the same row.
   *  Counted here rather than left to the browser's `dblclick`, which requires
   *  both clicks to land on the same DOM node — not guaranteed once the first
   *  click re-renders the grid row. 500 ms is the usual OS double-click window. */
  const pairedClick = useCallback((row: SelectedRow) => {
    const now = Date.now();
    const prev = lastFxClick.current;
    if (prev && prev.oid === row.oid && now - prev.at < 500) {
      lastFxClick.current = null;
      setFxRow(row);
      return;
    }
    lastFxClick.current = { oid: row.oid, at: now };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Available is filtered server-side to "not already selected for this
      // template"; with no template yet (a New record) it is the whole list.
      const q = parentId ? `?templateId=${encodeURIComponent(parentId)}` : '';
      const [av, sel] = await Promise.all([
        api.get(`/api/v1/daily-qc-logbook/available${q}`),
        parentId ? api.get(`/api/v1/daily-qc-logbook/${parentId}/selected`) : Promise.resolve(null),
      ]);
      setAvailable((av.data?.data ?? []) as AvailableRow[]);
      setSelected(((sel?.data?.data ?? []) as SelectedRow[]) ?? []);
      setPickLeft(new Set());
      setPickRight(new Set());
    } catch {
      notify.error('Failed to load field configuration.');
    } finally {
      setLoading(false);
    }
  }, [parentId, notify]);

  useEffect(() => { void load(); }, [load]);

  /** Write the buffered drafts to a template that has just been created.
   *
   *  Called BY THE SAVE, inside its awaited step, before the page switches to the
   *  saved record — the same contract the SDMS Sequence Setup grids use. An
   *  earlier version did this from a `useEffect` on parentId instead, which raced
   *  the reload and could run with an already-cleared buffer, so a New record
   *  saved its header and lost every field row (user report 2026-09-24). */
  const flushBuffered = useCallback(async (templateId: string) => {
    const drafts = pendingRef.current;
    if (drafts.length === 0) return;

    // 1. Create the rows (the endpoint takes ids and applies its own defaults).
    await api.post(`/api/v1/daily-qc-logbook/${templateId}/selected/add`,
      { ids: drafts.map((d) => d.oid) });

    // 2. Re-read them to learn the Oids SQL assigned, then replay any edit made
    //    while the record was still new. Matched BY NAME, which is exact:
    //    DailyQcDataTable.FieldName is unique (verified, 82/82 live rows) and the
    //    add copies that name onto the Selected row.
    const created = ((await api.get(
      `/api/v1/daily-qc-logbook/${templateId}/selected`)).data?.data ?? []) as SelectedRow[];
    const byName = new Map(created.map((r) => [r.name ?? '', r]));

    for (const draft of drafts) {
      const row = byName.get(draft.name ?? '');
      if (!row) continue;
      const changed =
        (draft.caption ?? null) !== (row.caption ?? null) ||
        (draft.assignedValue ?? null) !== (row.assignedValue ?? null) ||
        (draft.width ?? null) !== (row.width ?? null) ||
        (draft.sort ?? null) !== (row.sort ?? null) ||
        !!draft.freeze !== !!row.freeze ||
        !!draft.default !== !!row.default ||
        (draft.symbol ?? null) !== (row.symbol ?? null) ||
        (draft.formulaText ?? null) !== (row.formulaText ?? null);
      if (!changed) continue;  // untouched — the defaults already match
      await api.put(`/api/v1/daily-qc-logbook/selected/${row.oid}`, {
        caption: draft.caption ?? null,
        assignedValue: draft.assignedValue ?? null,
        width: draft.width ?? null,
        sort: draft.sort ?? null,
        freeze: !!draft.freeze,
        default: !!draft.default,
        symbol: draft.symbol ?? null,
        formulaText: draft.formulaText ?? null,
      });
    }
    setPending([]);
  }, []);

  // Hand the flush to the detail page, which runs it as part of Save.
  useEffect(() => {
    if (!flushRef) return;
    flushRef.current = flushBuffered;
    return () => { flushRef.current = null; };
  }, [flushRef, flushBuffered]);

  const add = async () => {
    if (pickLeft.size === 0) return;
    // No template yet: buffer instead of posting (see `pending`).
    if (!parentId) {
      // Seed each draft with what the server would apply on add (Width 100,
      // Sort = max+1), so what is shown before the save is what gets saved.
      setPending((p) => {
        const picked = available.filter((a) => pickLeft.has(a.oid));
        return [...p, ...picked.map((a, i) => ({
          oid: a.oid,
          name: a.fieldName,
          mappedProperty: a.mappedProperty,
          width: 100,
          sort: p.length + i + 1,
        }))];
      });
      setPickLeft(new Set());
      return;
    }
    try {
      await api.post(`/api/v1/daily-qc-logbook/${parentId}/selected/add`, { ids: [...pickLeft] });
      notify.success('Saved successfully.');
      await load();
    } catch {
      notify.error('Failed to add the selected field(s).');
    }
  };

  const remove = async () => {
    if (pickRight.size === 0) return;
    // Nothing is persisted yet, so dropping a buffered pick needs no confirm —
    // there is nothing to undo and no data to lose.
    if (!parentId) {
      setPending((p) => p.filter((r) => !pickRight.has(r.oid)));
      setPickRight(new Set());
      return;
    }
    const ok = await confirm({
      title: 'Remove fields',
      message: `Remove ${pickRight.size} field(s) from this template?`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/api/v1/daily-qc-logbook/${parentId}/selected/remove`, { ids: [...pickRight] });
      notify.success('Deleted successfully.');
      await load();
    } catch {
      notify.error('Failed to remove the selected field(s).');
    }
  };

  /** Persist one row's editable set. The endpoint takes them as a unit, so the
   *  whole row goes out with the one changed value already applied. */
  const commit = useCallback(async (row: SelectedRow) => {
    try {
      await api.put(`/api/v1/daily-qc-logbook/selected/${row.oid}`, {
        caption: row.caption ?? null,
        assignedValue: row.assignedValue ?? null,
        width: row.width ?? null,
        sort: row.sort ?? null,
        freeze: !!row.freeze,
        default: !!row.default,
        symbol: row.symbol ?? null,
        // ⚠️ Must always be sent. The endpoint takes the editable set as a UNIT and
        // writes FormulaText unconditionally, so omitting it here would clear the
        // formula every time an unrelated cell (a Caption, a Width) was edited.
        formulaText: row.formulaText ?? null,
      });
    } catch {
      notify.error('Failed to save the field.');
      await load();
    }
  }, [notify, load]);

  /** Apply an edit locally, then persist that row.
   *
   *  The patched row is built from `selectedRef`, NOT from inside the setSelected
   *  updater: React does not run an updater synchronously, so reading the result
   *  out of it left nothing to persist and the save silently never fired. The ref
   *  also keeps this callback stable, so the column definitions are not rebuilt
   *  (and column widths reset) on every keystroke. */
  const apply = useCallback((oid: string, patch: Partial<SelectedRow>) => {
    // A draft on an unsaved record: edit the buffer only. There is no Selected row
    // to PUT to yet — these values travel to the server with the first save.
    if (!parentIdRef.current) {
      setPending((rows) => rows.map((r) => (r.oid === oid ? { ...r, ...patch } : r)));
      return;
    }
    const current = selectedRef.current.find((r) => r.oid === oid);
    if (!current) return;
    const next = { ...current, ...patch };
    setSelected((rows) => rows.map((r) => (r.oid === oid ? next : r)));
    void commit(next);
  }, [commit]);

  /** Available, minus anything buffered on a new record — so a field leaves the
   *  left list the moment it is moved, exactly as it does once saved (where the
   *  server computes "not already selected"). */
  const availableRows = useMemo<AvailableRow[]>(() => {
    if (pending.length === 0) return available;
    const taken = new Set(pending.map((p) => p.oid));
    return available.filter((a) => !taken.has(a.oid));
  }, [available, pending]);

  /** Selected, or the buffer while the record is new. The buffered rows carry the
   *  DailyQcDataTable Oid, NOT a Selected-row Oid, so they are shown with the
   *  defaults the server would apply (Width 100, Sort in pick order) and are NOT
   *  inline-editable — an edit would address a row that does not exist yet. */
  const selectedRows = useMemo<SelectedRow[]>(
    () => (parentId ? selected : pending), [parentId, selected, pending]);

  /** What the Formula Editor's Symbol pane offers: the rows of THIS template that
   *  have been given a Symbol — legacy builds the same list from the same grid.
   *  Drafts count too, so a formula can be built before the first save. */
  const fxSymbols = useMemo<FormulaSymbol[]>(
    () => selectedRows
      .filter((r) => (r.symbol ?? '').trim() !== '')
      .map((r) => ({ oid: r.oid, name: r.name, symbol: (r.symbol ?? '').trim() })),
    [selectedRows],
  );

  const availableCols = useMemo<AppColumn<AvailableRow>[]>(() => [
    { field: 'fieldName', header: 'Name', sortable: true, filterable: true },
  ], []);

  const selectedCols = useMemo<AppColumn<SelectedRow>[]>(() => {
    // Name / Mapped Property are copied from the Available row and are read-only in
    // legacy; Formula is owned by the Formula Editor popup below.
    const ro = viewOnly;
    /** Freeze / Default are real checkboxes, as legacy renders them. */
    const boolCell = (key: 'freeze' | 'default') => (r: SelectedRow) => (
      <input type="checkbox" checked={!!r[key]} disabled={ro} aria-label={key}
        onChange={(e) => apply(r.oid, { [key]: e.target.checked } as Partial<SelectedRow>)} />
    );
    /** Formula is a checkbox too (G5 parity) but is never clicked directly — it is
     *  read-only in legacy as well (AllowEdit="False"). The flag is DERIVED from
     *  the formula text, so the way to set it is the Formula Editor, opened from
     *  the cell (legacy opens it from this cell too).
     *
     *  The popup opens in the READ-ONLY view as well — it just cannot be saved
     *  there. Gating the double-click on edit mode meant a user looking at a
     *  record got no response at all, which reads as a broken control rather
     *  than as a disabled one. */
    const formulaCell = (r: SelectedRow) => (
      <div
        className="dqc-fx-cell"
        // Two ways in, because ONE is not reliable here. The first click focuses
        // the grid row, which re-renders it — if React replaces this node the
        // browser never emits `dblclick` (it needs both clicks on the SAME
        // element), so the popup silently never opened. `onDoubleClick` is kept
        // for when the node does survive; `pairedClick` counts the two clicks in
        // a ref instead, which no re-render can disturb.
        onClick={() => pairedClick(r)}
        onDoubleClick={() => setFxRow(r)}
        title={`${r.formulaText ? `Formula: ${r.formulaText}` : 'No formula'} — double-click to ${ro ? 'view' : 'edit'}`}
      >
        <input type="checkbox" checked={!!r.formula} disabled aria-label="Formula" />
      </div>
    );
    // ⚠️ All 10 columns fit WITHOUT horizontal scrolling AND keep their per-column
    // filter, as legacy does. Both only fit because Available gave up its pager
    // (it is a count-only pick-list now) and this grid runs tighter cell padding —
    // measured: each header costs its label + ~29px of sort/filter icons + padding,
    // so the icons alone are ~290px across the ten.
    return [
      { field: "name",           header: "Name",            width: 82, sortable: true, filterable: true },
      { field: "caption",        header: "Caption",         width: 93, sortable: true, filterable: true,
        edit: ro ? undefined : { type: "text" } },
      { field: "mappedProperty", header: "Mapped Property", width: 106, sortable: true, filterable: true },
      { field: "assignedValue",  header: "Assigned Value",  width: 100, sortable: true, filterable: true,
        edit: ro ? undefined : { type: "text" } },
      { field: "width",  header: "Width", width: 83, align: "right", type: "number",
        sortable: true, filterable: true, edit: ro ? undefined : { type: "number" } },
      { field: "sort",   header: "Sort",  width: 72, align: "right", type: "number",
        sortable: true, filterable: true, edit: ro ? undefined : { type: "number" } },
      { field: "freeze", header: "Freeze", width: 85, align: "center", sortable: true, filterable: true,
        render: boolCell("freeze"), text: (r) => (r.freeze ? "Yes" : "No") },
      { field: "default", header: "Default", width: 90, align: "center", sortable: true, filterable: true,
        render: boolCell("default"), text: (r) => (r.default ? "Yes" : "No") },
      { field: "symbol",  header: "Symbol",  width: 91, sortable: true, filterable: true,
        edit: ro ? undefined : { type: "text" } },
      { field: "formula", header: "Formula", width: 95, align: "center", sortable: true, filterable: true,
        render: formulaCell, text: (r) => (r.formula ? "Yes" : "No") },
    ];
  }, [viewOnly, apply, pairedClick]);

  /** Route a shared-grid inline edit back through the row's own save. */
  const onCellCommit = (row: SelectedRow, column: AppColumn<SelectedRow>, raw: string) => {
    const f = column.field;
    if (f === 'width' || f === 'sort') {
      const n = raw.trim() === '' ? null : Number(raw);
      apply(row.oid, { [f]: Number.isFinite(n as number) ? n : null } as Partial<SelectedRow>);
    } else {
      apply(row.oid, { [f]: raw } as Partial<SelectedRow>);
    }
  };

  return (
    <div className="sd-child">
      <h3 className="sd-sub-h">Field Configuration</h3>

      <div className="dqc-dual">
        {/* ── Available ──
            A pick-list, not a report: legacy shows it as ONE scrolling list with a
            plain "Count=69" and no pager at all. So every row renders on a single
            page and the shared footer is hidden (CSS), replaced by the count below
            — which also hands its width back to Selected, where it buys the
            per-column filters (user request 2026-09-24). */}
        <div className="dqc-pane">
          <div className="dqc-pane-h"><span>Available</span></div>
          <AppDataGrid<AvailableRow>
            rows={availableRows}
            columns={availableCols}
            getRowId={(r) => r.oid}
            loading={loading}
            selectable={!viewOnly}
            selectedIds={pickLeft}
            onSelectionChange={setPickLeft}
            defaultPageSize={ALL_PAGE_SIZE}
            maximizable
            exportFilename="Available Fields"
            emptyText="No data to display"
          />
          <div className="dqc-count">Count={availableRows.length}</div>
        </div>

        {/* ── add / remove — legacy's green + / red − ── */}
        <div className="dqc-mid">
          <button type="button" className="dqc-move dqc-move--add" aria-label="Add selected fields"
            title={parentId
              ? 'Add the checked fields to this template'
              : 'Add the checked fields — they are written when the template is saved'}
            disabled={viewOnly || pickLeft.size === 0}
            onClick={() => void add()}>+</button>
          <button type="button" className="dqc-move dqc-move--del" aria-label="Remove selected fields"
            title="Remove the checked fields from this template"
            disabled={viewOnly || pickRight.size === 0}
            onClick={() => void remove()}>&#8722;</button>
        </div>

        {/* ── the legacy Formula Editor, opened from the Formula cell ── */}
        <FormulaEditorDialog
          open={!!fxRow}
          initialText={fxRow?.formulaText ?? ''}
          symbols={fxSymbols}
          readOnly={viewOnly}
          onCancel={() => setFxRow(null)}
          onSave={(formulaText) => {
            const row = fxRow;
            setFxRow(null);
            // The Formula flag is DERIVED server-side from the text, so it is
            // mirrored the same way here rather than sent — the two cannot drift.
            if (row) apply(row.oid, { formulaText, formula: formulaText !== '' });
          }}
        />

        {/* ── Selected ── */}
        <div className="dqc-pane dqc-pane--wide">
          <div className="dqc-pane-h"><span>Selected</span></div>
          <AppDataGrid<SelectedRow>
            rows={selectedRows}
            columns={selectedCols}
            getRowId={(r) => r.oid}
            loading={loading}
            selectable={!viewOnly}
            selectedIds={pickRight}
            onSelectionChange={setPickRight}
            onCellCommit={onCellCommit}
            maximizable
            exportFilename="Field Configuration"
            emptyText="No data to display"
          />
        </div>
      </div>
    </div>
  );
}
